"use client";

import { c as _c } from "react/compiler-runtime";
import { EmptyState, Media, SideSheet, SideSheetBody, SideSheetHeader, transitions } from "@opensea/ui-kit";
import { useMountEffect } from "@opensea/ui-kit/hooks";
import { Close } from "@opensea/ui-kit/icons";
import { MotionConfig } from "motion/react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslations } from "use-intl";
import { useSnapshot } from "valtio";
import { Button } from "../../components/Button";
import { useTrackError } from "../../providers/TrackErrorProvider";
import { FlowActions, FlowState } from "../../stores/flowStore";
import { useIsFlowActive } from "../hooks/useIsFlowActive";
import { DepositFlowContent } from "./DepositFlow/DepositFlowContent";
import { FiatCheckoutFlowContent } from "./FiatCheckoutFlow/FiatCheckoutFlowContent";
import { SendProvider } from "./SendFlow/provider/SendProvider";
import { SendFlowContent } from "./SendFlow/SendFlowContent";
import { SwapFlow } from "./SwapFlow/SwapFlow";
import { TopupFlowContent } from "./TopupFlow/TopupFlowContent";
import { ViewWalletFlowContent } from "./ViewWalletFlow/ViewWalletFlowContent";
export const SideSheetFlows = () => {
  const $ = _c(26);
  const {
    flow
  } = useSnapshot(FlowState);
  let t0;
  if ($[0] !== flow) {
    t0 = () => {
      switch (flow?.type) {
        case "view-wallet":
          {
            return <ViewWalletFlowContent address={flow.address} switchTab={_temp} tab={flow.tab} />;
          }
        case "send":
          {
            return <SendProvider><MotionConfig transition={transitions.default}><SendFlowContent onBack={flow.onBack} /></MotionConfig></SendProvider>;
          }
        case "swap":
          {
            return <SwapFlow initialFromCurrency={flow.initialFromCurrency} initialToCurrency={flow.initialToCurrency} onBack={flow.onBack} onClose={FlowActions.endFlow} />;
          }
        case "deposit":
          {
            return <DepositFlowContent onBack={flow.onBack} />;
          }
        case "topup":
          {
            return <TopupFlowContent onBack={flow.onBack} />;
          }
        case "fiat-checkout":
          {
            return <FiatCheckoutFlowContent item={flow.item} listingId={flow.listingId} onBack={flow.onBack} onTransactionCreated={flow.onTransactionCreated} />;
          }
        default:
          {
            return null;
          }
      }
    };
    $[0] = flow;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const renderContent = t0;
  const isViewWalletActive = useIsFlowActive("view-wallet");
  const isSendActive = useIsFlowActive("send");
  const isSwapActive = useIsFlowActive("swap");
  const isDepositActive = useIsFlowActive("deposit");
  const isTopupActive = useIsFlowActive("topup");
  const isFiatCheckoutActive = useIsFlowActive("fiat-checkout");
  const isSideSheetFlowActive = isViewWalletActive || isSendActive || isSwapActive || isDepositActive || isTopupActive || isFiatCheckoutActive;
  const t1 = flow?.type;
  let t2;
  if ($[2] !== t1) {
    t2 = [t1];
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== renderContent) {
    t3 = renderContent();
    $[4] = renderContent;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <SideSheetErrorBoundary resetKeys={t2}>{t3}</SideSheetErrorBoundary>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isSideSheetFlowActive || $[10] !== t4) {
    t5 = <Media greaterThanOrEqual="md"><SideSheet content={t4} direction="right" onOpenChange={_temp2} open={isSideSheetFlowActive} /></Media>;
    $[9] = isSideSheetFlowActive;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = flow?.type;
  let t7;
  if ($[12] !== t6) {
    t7 = [t6];
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== renderContent) {
    t8 = renderContent();
    $[14] = renderContent;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <SideSheetErrorBoundary resetKeys={t7}>{t8}</SideSheetErrorBoundary>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== isSideSheetFlowActive || $[20] !== isViewWalletActive || $[21] !== t9) {
    t10 = <Media lessThan="md"><SideSheet content={t9} direction="right" onOpenChange={_temp3} open={isSideSheetFlowActive} withCloseIcon={isViewWalletActive} /></Media>;
    $[19] = isSideSheetFlowActive;
    $[20] = isViewWalletActive;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t5) {
    t11 = <>{t5}{t10}</>;
    $[23] = t10;
    $[24] = t5;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
};
const SideSheetErrorFallback = t0 => {
  const $ = _c(17);
  const {
    error,
    resetErrorBoundary
  } = t0;
  const t = useTranslations("wallet.SideSheetFlows");
  const trackError = useTrackError();
  let t1;
  if ($[0] !== error || $[1] !== trackError) {
    t1 = () => {
      trackError(error, {
        tags: {
          flow: FlowState.flow?.type ?? "",
          source: "SideSheetErrorFallback"
        }
      });
    };
    $[0] = error;
    $[1] = trackError;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useMountEffect(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SideSheetHeader><Media lessThan="md"><Button className="ml-auto h-8 p-1.5" onClick={_temp4} variant="ghost"><Close size={20} /></Button></Media></SideSheetHeader>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("description");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("reset");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== resetErrorBoundary || $[9] !== t4) {
    t5 = <Button onClick={resetErrorBoundary} variant="secondary">{t4}</Button>;
    $[8] = resetErrorBoundary;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("title");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t5 || $[15] !== t6) {
    t7 = <>{t2}<SideSheetBody className="h-full"><EmptyState description={t3} extra={t5} size="lg" title={t6} variant="no-data" /></SideSheetBody></>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
const SideSheetErrorBoundary = t0 => {
  const $ = _c(3);
  const {
    children,
    resetKeys
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== resetKeys) {
    t1 = <ErrorBoundary FallbackComponent={SideSheetErrorFallback} resetKeys={resetKeys}>{children}</ErrorBoundary>;
    $[0] = children;
    $[1] = resetKeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function _temp(tab) {
  FlowActions.setFlowField("tab", tab);
}
function _temp2(open) {
  if (!open) {
    FlowActions.endFlow();
  }
}
function _temp3(open_0) {
  if (!open_0) {
    FlowActions.endFlow();
  }
}
function _temp4() {
  FlowActions.endFlow();
}